/**
 * Potvrzení uložení, aby bylo jasné i v rukavicích a na slunci:
 *  - velká zelená fajfka přes obrazovku (vždy),
 *  - zavibrování (Android; na iPhonu jemné „ťuknutí“ přes systémový přepínač, kde to iOS dovolí),
 *  - krátký tón (volitelně).
 * Volby jsou v Nastavení → Potvrzení uložení.
 */
import { useEffect, useState } from 'react';
import { getSetting, setSetting } from '../db/db';
import { Section, toast } from './ui';

export const FX_SETTING = 'confirmFx';

export interface FxSettings {
  vibrate: boolean;
  sound: boolean;
}

const DEFAULT_FX: FxSettings = { vibrate: true, sound: false };
let fx: FxSettings = DEFAULT_FX;

export async function loadFx() {
  fx = { ...DEFAULT_FX, ...((await getSetting<FxSettings>(FX_SETTING)) ?? {}) };
  return fx;
}

// ---------------------------------------------------------------------------
// Haptika
// ---------------------------------------------------------------------------

let iosSwitch: HTMLLabelElement | null = null;

/** iOS 18+: klepnutí na přepínač <input switch> vyvolá jemnou odezvu Taptic Engine. */
function iosTap() {
  try {
    if (!iosSwitch) {
      iosSwitch = document.createElement('label');
      iosSwitch.setAttribute('aria-hidden', 'true');
      iosSwitch.style.cssText = 'position:fixed;left:-9999px;top:0;width:1px;height:1px;overflow:hidden;opacity:0;pointer-events:none';
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.setAttribute('switch', '');
      input.tabIndex = -1;
      iosSwitch.appendChild(input);
      document.body.appendChild(iosSwitch);
    }
    iosSwitch.click();
  } catch {
    /* nevadí */
  }
}

export function haptic(pattern: number | number[] = 40) {
  if (!fx.vibrate) return;
  if (typeof navigator.vibrate === 'function') navigator.vibrate(pattern);
  else iosTap();
}

// ---------------------------------------------------------------------------
// Zvuk
//
// iPhone (Safari) pustí zvuk z webové aplikace jen tehdy, když se zvukový výstup
// „odemkne“ dotykem uživatele. Po přepnutí do jiné aplikace nebo zhasnutí displeje
// ho znovu uspí. Proto ho při každém dotyku znovu probudíme a zahrajeme krátké ticho.
// Pozor: s přepínačem Tichý režim na boku iPhonu zůstane zvuk z webu vypnutý.
// ---------------------------------------------------------------------------

let audio: AudioContext | null = null;

function ensureCtx(): AudioContext | null {
  try {
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    const state = audio?.state as string | undefined;
    if (!audio || state === 'closed') audio = new AC();
    return audio;
  } catch {
    return null;
  }
}

/** Volá se při dotyku: probudí zvukový výstup a „odemkne“ ho tichým tónem. */
function unlockAudio() {
  if (!fx.sound) return;
  const a = ensureCtx();
  if (!a) return;
  if (a.state !== 'running') void a.resume().catch(() => {});
  try {
    const b = a.createBuffer(1, 1, 22050);
    const src = a.createBufferSource();
    src.buffer = b;
    src.connect(a.destination);
    src.start(0);
  } catch {
    /* nevadí */
  }
}

function playChime(a: AudioContext) {
  const t = a.currentTime + 0.02;
  [
    [880, 0],
    [1320, 0.09],
  ].forEach(([f, d]) => {
    const o = a.createOscillator();
    const g = a.createGain();
    o.type = 'sine';
    o.frequency.value = f;
    g.gain.setValueAtTime(0.0001, t + d);
    g.gain.exponentialRampToValueAtTime(0.3, t + d + 0.015);
    g.gain.exponentialRampToValueAtTime(0.0001, t + d + 0.22);
    o.connect(g).connect(a.destination);
    o.start(t + d);
    o.stop(t + d + 0.25);
  });
}

/** Krátký dvoutónový „cink“. */
export function chime() {
  if (!fx.sound) return;
  const a = ensureCtx();
  if (!a) return;
  if (a.state === 'running') playChime(a);
  else
    a.resume()
      .then(() => playChime(a))
      .catch(() => {});
}

export function installFx() {
  void loadFx();
  // každý dotyk / klávesa udrží zvuk připravený (i po návratu z jiné aplikace)
  for (const ev of ['pointerdown', 'touchend', 'keydown'] as const) addEventListener(ev, unlockAudio, { capture: true, passive: true });
}

// ---------------------------------------------------------------------------
// Velká fajfka
// ---------------------------------------------------------------------------

let showImpl: ((text: string) => void) | null = null;

/**
 * Uloženo: velká fajfka + vibrace + případně zvuk. Používá se u každého uložení
 * (zápisy, včelstva, stanoviště, nastavení, zálohy, mazání).
 * S `undo` se navíc ukáže oznámení s tlačítkem Vrátit.
 */
export function saved(text = 'Uloženo', undo?: { label: string; run: () => void }) {
  haptic(40);
  chime();
  if (showImpl) showImpl(text);
  else if (!undo) toast(text);
  if (undo) toast(text, undo);
}

/** Uložení změny v Nastavení – stejné potvrzení jako u zápisů. */
export function confirmSmall(text = 'Uloženo') {
  saved(text);
}

export function SavedHost() {
  const [text, setText] = useState<string | null>(null);
  const [key, setKey] = useState(0);
  useEffect(() => {
    let timer: number | undefined;
    showImpl = (t) => {
      setText(t);
      setKey((k) => k + 1);
      clearTimeout(timer);
      timer = window.setTimeout(() => setText(null), 1300);
    };
    return () => {
      showImpl = null;
      clearTimeout(timer);
    };
  }, []);
  if (!text) return null;
  return (
    <div className="saved-overlay" role="status" aria-live="assertive" key={key} onClick={() => setText(null)}>
      <div className="saved-box">
        <svg viewBox="0 0 52 52" className="saved-check" aria-hidden>
          <circle cx="26" cy="26" r="24" />
          <path d="M15 27 l7 7 l15 -16" />
        </svg>
        <div className="saved-text">{text}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Nastavení
// ---------------------------------------------------------------------------

export function FxSetting() {
  const [s, setS] = useState<FxSettings | null>(null);
  useEffect(() => {
    loadFx().then(setS);
  }, []);
  if (!s) return null;
  const update = async (p: Partial<FxSettings>) => {
    const next = { ...s, ...p };
    setS(next);
    fx = next;
    await setSetting(FX_SETTING, next);
    saved(p.sound === false ? 'Zvuk vypnut' : p.vibrate === false ? 'Vibrace vypnuty' : 'Uloženo');
  };
  const canVibrate = typeof navigator.vibrate === 'function';
  return (
    <Section title="Potvrzení uložení">
      <p className="muted">Po uložení zápisu se vždy ukáže velká zelená fajfka. Navíc můžete zapnout:</p>
      <label className="check">
        <input type="checkbox" checked={s.vibrate} onChange={(e) => update({ vibrate: e.target.checked })} />
        Zavibrovat
      </label>
      {!canVibrate && (
        <p className="small muted">
          iPhone webovým aplikacím vibrace nepovoluje. Na novějších iPhonech (iOS 18 a novější) se místo toho zkusí jemné „ťuknutí“ –
          pokud ho necítíte, telefon to neumožňuje.
        </p>
      )}
      <label className="check">
        <input type="checkbox" checked={s.sound} onChange={(e) => update({ sound: e.target.checked })} />
        Krátký zvuk
      </label>
      {s.sound && (
        <p className="small muted">
          Na iPhonu zvuk nehraje, když je zapnutý tichý režim (přepínač na boku telefonu) nebo je hlasitost stažená na nulu.
        </p>
      )}
      <div className="quick-row">
        <button className="btn" onClick={() => saved('Takhle to vypadá')}>
          Vyzkoušet
        </button>
      </div>
    </Section>
  );
}
